import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Pause, Play } from "lucide-react";
import { TRACKS } from "@/data/site";

const EqBars = () => (
  <span className="flex items-end gap-[3px] h-4" aria-hidden="true">
    {[0, 1, 2, 3].map((i) => (
      <span key={i} className="eq-bar w-[3px] h-full bg-gold" style={{ animationDelay: `${i * 0.15}s` }} />
    ))}
  </span>
);

const playerSrc = (url) =>
  `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23d4af37&auto_play=true&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=false`;

export const TrackList = ({ idPrefix = "track" }) => {
  const [playing, setPlaying] = useState(null);

  return (
    <div className="divide-y divide-amber-500/10 border border-amber-500/15 bg-surface/60">
      {TRACKS.map((t, i) => {
        const active = playing === t.url;
        return (
          <div key={t.url} data-testid={`${idPrefix}-item-${i}`}>
            <div className="flex items-center gap-4 px-5 py-4 hover:bg-surface2/60 transition-colors group">
              <button
                data-testid={`${idPrefix}-row-${i}`}
                onClick={() => setPlaying(active ? null : t.url)}
                aria-label={active ? `Pause ${t.title}` : `Play ${t.title}`}
                className="flex-1 min-w-0 flex items-center gap-4 text-left"
              >
                <span className="w-8 h-8 shrink-0 flex items-center justify-center border border-amber-500/30 text-amber-300 group-hover:border-amber-400/70 transition-colors">
                  {active ? <EqBars /> : <Play size={12} className="ml-0.5" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-serif text-lg text-amber-100 truncate">{t.title}</span>
                  <span className="block text-xs text-slate-500 truncate">{t.description}</span>
                </span>
              </button>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-600 hidden sm:block">{t.category}</span>
              <a
                href={t.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${idPrefix}-soundcloud-link-${i}`}
                aria-label={`Open ${t.title} on SoundCloud`}
                className="shrink-0 text-slate-500 hover:text-amber-200 transition-colors"
              >
                <ArrowUpRight size={16} />
              </a>
            </div>
            <AnimatePresence initial={false}>
              {active && (
                <motion.div
                  key="player"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-5">
                    <iframe
                      data-testid={`${idPrefix}-player-${i}`}
                      title={`${t.title} — SoundCloud player`}
                      width="100%"
                      height="166"
                      scrolling="no"
                      frameBorder="no"
                      allow="autoplay"
                      src={playerSrc(t.url)}
                      className="block border border-amber-500/15"
                    />
                    <button
                      onClick={() => setPlaying(null)}
                      data-testid={`${idPrefix}-close-${i}`}
                      className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-amber-200 transition-colors"
                    >
                      <Pause size={11} /> Close player
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};
